import { tl } from 'common/services/i18nService';
import { decodeDisplayText } from 'common/util/text/textUtil';
import moment from 'moment';
import { UserPrivileges } from 'common/store/session/constants';
import store from '../store';
import { RootState } from '../store/rootReducer';
import {
    getCurrentUserPrivileges,
    hasCurrentUserEvaluatorPrivilege,
    hasCurrentUserPlannerPrivilege,
} from './permissions/PermissionService';
import { Evaluator } from '../store/assessment/reducer';
import { getPartyCommitteeIdForContext } from './gradingPartyService';

export interface Committee {
    committeeId: number;
    name: string;
    candidateCount: number;
    evaluators: any[];
    gradingCompleted: boolean;
    moderationStatus: string;
    moderationComment: string;
    type: keyof typeof gradingTypes;
    deadline: string;
    marksMode: string;
    confirmationMode: string;
    gradingLevel: string;
    userRoles: string[];
    isAllCommitteeRow?: boolean;
    candidateIds: any[];
    isMarkingTeamCommittee?: boolean;
    gradingAllowedForCommittee?: boolean;
    gradingQuestionAssignmentAccessLevel?: string;
    gradingQuestionAssignmentAdminLevel?: string;
    gradeTransferCanReturn?: boolean;
    gradeTransferTimestamp?: string;
    hasGradesToTransfer?: boolean;
}

enum gradingStatus {
    COMPLETED = 'completed',
    NOT_COMPLETED = 'not_completed',
}

export enum gradingTypes {
    GRADING = 'grading',
    REASSESSMENT = 'appeal',
}

export const getGradingType = (type: keyof typeof gradingTypes): string => {
    return type ? tl(`grading.committees.${gradingTypes[type]}`) : '';
};

export const getGradingStatus = (gradingCompleted: boolean): string => {
    return tl(`grading.committee.gradingStatus.${gradingCompleted
        ? gradingStatus.COMPLETED : gradingStatus.NOT_COMPLETED}`);
};

export const getCommitteeById = (committeeId: number): any => {
    const { assessment } = (store.getState() as RootState);
    if (!assessment.committees) {
        return null;
    }

    return assessment.committees.find((committee) => committee.committeeId === committeeId);
};

export const isCurrentUserEvaluatorInSelectedCommittee = (): boolean => {
    const { context } = (store.getState() as RootState);
    const selectedCommitteeId = getPartyCommitteeIdForContext(context.partyState.gradingPartyDocument);
    const currentCommittee = getCommitteeById(selectedCommitteeId);

    return currentCommittee && currentCommittee.belongs;
};

export const getUserRolesInSelectedCommittee = (): string[] => {
    const { assessment, context } = (store.getState() as RootState);
    const { partyState: { gradingPartyDocument } } = context;
    const selectedCommitteeId = getPartyCommitteeIdForContext(gradingPartyDocument);

    if (selectedCommitteeId > 0) {
        const belongsCommittees = getBelongedCommittees(assessment.committees);
        const selectedCommittee = belongsCommittees.find((committee: Committee) => {
            return committee.committeeId === selectedCommitteeId;
        });
        return selectedCommittee?.userRoles;
    }

    return getCurrentUserPrivileges();
};

export const getUserRolesInCommittee = (committee: any): string[] => {
    const userRoleInCommittee = [];

    if (hasCurrentUserPlannerPrivilege()) {
        userRoleInCommittee.push(UserPrivileges.ar_plan);
    }
    if (hasCurrentUserEvaluatorPrivilege() && committee?.belongs) {
        userRoleInCommittee.push(UserPrivileges.ar_evaluate);
    }

    return userRoleInCommittee;
};

export const createAllCommittee = (): Committee => {
    return {
        candidateCount: getCandidatesCountForAllCommittees(),
        evaluators: [],
        committeeId: -1,
        name: 'allCommittees',
        gradingCompleted: null,
        moderationComment: null,
        moderationStatus: null,
        type: null,
        marksMode: null,
        confirmationMode: null,
        gradingLevel: null,
        deadline: '-',
        userRoles: getUserRolesInCommittee({ belongs: false }),
        isAllCommitteeRow: true,
        candidateIds: getUniqueCandidatesForAllCommittees(),
    };
};

export const createMarkingTeamFakeCommittee = (): Committee => {
    const { assessment, session: { user }, context: { partyId } } = (store.getState() as RootState);
    return {
        candidateCount: assessment.totalNumberOfDeliveriesCovered,
        evaluators: assessment.evaluators as [],
        committeeId: 0,
        name: 'markingTeam',
        gradingCompleted: null,
        moderationComment: null,
        moderationStatus: null,
        type: 'GRADING',
        marksMode: assessment.marksMode,
        confirmationMode: assessment.confirmationMode,
        gradingLevel: assessment.gradingLevel,
        deadline: '-',
        userRoles: user.privileges,
        isAllCommitteeRow: false,
        isMarkingTeamCommittee: true,
        candidateIds: [partyId],
    };
};

export const getBelongedCommittees = (committees: any) => {
    if (committees) {
        let belongedCommittees = committees;

        if (!hasCurrentUserPlannerPrivilege() && hasCurrentUserEvaluatorPrivilege()) {
            belongedCommittees = committees.filter((committee: any) => committee.belongs);
        }

        return belongedCommittees.map((committee: any) => {
            const {
                committeeId, candidateCount, gradingCompleted, type, upcomingDeadline, marksMode, confirmationMode,
                moderationComment, moderationStatus, evaluators, name, gradingLevel, candidateIds,
            } = committee;
            const deadline = upcomingDeadline ? moment(upcomingDeadline).format('DD.MM.YYYY') : '-';
            const userRoles = getUserRolesInCommittee(committee);

            return {
                candidateCount,
                committeeId,
                name,
                evaluators,
                gradingCompleted,
                moderationComment,
                moderationStatus,
                type,
                marksMode,
                confirmationMode,
                gradingLevel,
                deadline,
                userRoles,
                candidateIds,
            };
        });
    }

    return [createMarkingTeamFakeCommittee()];
};

export const getCandidatesCountForAllCommittees = (): number => {
    const { assessment } = (store.getState() as RootState);

    // works for both planner or grader
    return assessment.totalNumberOfDeliveriesCovered;
};

export const getUniqueCandidatesForAllCommittees = () => {
    const { assessment } = (store.getState() as RootState);
    const belongedCommittees = getBelongedCommittees(assessment.committees);
    const allUniqueCandidateIds: any[] = [...new Set(belongedCommittees.map((committee: Committee) => committee.candidateIds).flat())];
    return allUniqueCandidateIds;
};

export const getCommitteeNameByID = (committeeId: number): string => {
    const currentCommittee = getCommitteeById(committeeId);

    return currentCommittee ? decodeDisplayText(currentCommittee.name) : null;
};

export const getCustomisedCommitteeNameByID = (committeeId: number): string => {
    const committeeName = getCommitteeNameByID(committeeId);

    return committeeName ? `${tl('grading.header.committee')} ${committeeName}`
        : tl('grading.header.link.openCommitteeSelector');
};

export const getNumberOfEvaluators = (committeeId?: number): number => {
    const { assessment } = (store.getState() as RootState);
    if (committeeId > 0) {
        const committee: Committee = getCommitteeById(committeeId);
        return committee?.evaluators?.length;
    }

    return assessment.numberOfEvaluatorContributors;
};

export const getEvaluators = (committeeId?: number): Evaluator[] => {
    const { assessment } = (store.getState() as RootState);
    if (committeeId > 0) {
        const committee: Committee = getCommitteeById(committeeId);
        return committee?.evaluators;
    }
    return assessment?.evaluators;
};

export const getEvaluatorById = (evaluatorId: number, partyCommitteeId?: number) => {
    const evaluators = getEvaluators(partyCommitteeId);
    let evaluator = null;
    if (Array.isArray(evaluators)) {
        evaluator = evaluators.find((item: any) => item.id === evaluatorId);
    }
    return evaluator;
};

export const getEvaluatorInCommitteeId = (evaluatorId: number, committeeId: number): any => {
    const committee = getCommitteeById(committeeId);
    return committee?.evaluators?.find((evaluator: Evaluator) => evaluator.id === evaluatorId);
};

export const getEvaluatorsNames = (committeeId?: number, withoutCurrent?: boolean): string[] => {
    const { assessment, session } = (store.getState() as RootState);
    const { user } = session;
    let evaluators;
    if (assessment.committees && committeeId > 0) {
        const committee: Committee = getCommitteeById(committeeId);
        evaluators = [...committee.evaluators];
    } else {
        evaluators = [...assessment.evaluators];
    }
    if (withoutCurrent) {
        evaluators = evaluators.filter((evaluator: any) => evaluator.id !== user.id);
    }
    return evaluators.map((evaluator: any) => evaluator.name);
};

export const getCandidateCountBySelectedCommitteeId = (selectedCommitteeId: number) => {
    if (selectedCommitteeId <= 0) {
        return getCandidatesCountForAllCommittees();
    }

    const committee = getCommitteeById(selectedCommitteeId);
    return committee?.candidateCount;
};
